import React, { useState, useEffect } from "react";
import { cloneDeep } from "lodash";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import MuiLink from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Bar } from "react-chartjs-2";
import MappingSetting from "components/SettingFields/molecules/MappingSetting";
import BooleanSetting from "components/SettingFields/atoms/BooleanSetting";
import InputSetting from "components/SettingFields/atoms/InputSetting";
import NotificationBuilderSetting from "components/SettingFields/molecules/NotificationBuilderSetting";
import SyncFrequencySetting from "components/SettingFields/molecules/SyncFrequencySetting";
import SingleSelectDropdown from "components/SettingFields/atoms/SingleSelectDropdown";
import MultiSelectDropdown from "components/SettingFields/atoms/MultiSelectDropdown";
import { FormProvider, useForm } from "react-hook-form";
import SettingsTable from "components/SettingFields/molecules/SettingsTable";
import { enqueueSnackbar } from "notistack";
import HtmlSetting from "components/SettingFields/molecules/HtmlSetting";

const settingsDataTypeToComponent = {
  boolean: {
    Component: BooleanSetting,
  },
  mapping: {
    Component: MappingSetting,
  },
  sync_frequency: {
    Component: SyncFrequencySetting,
  },
  input: {
    Component: InputSetting,
  },
  notification_builder: {
    Component: NotificationBuilderSetting,
  },
  single_select_dropdown: {
    Component: SingleSelectDropdown,
  },
  multi_select_dropdown: {
    Component: MultiSelectDropdown,
  },
  table: {
    Component: SettingsTable,
  },
  html: {
    Component: HtmlSetting,
  },
};

const reconcileDiff = (orig, changeSet) => {
  const origDict = orig.reduce((dict, setting) => {
    dict[setting.en_field_label] = setting;
    return dict;
  }, {});

  return changeSet.filter((settingToCompare) => {
    const startingValue = JSON.stringify(origDict[settingToCompare.en_field_label].field_value);
    const currentValue = JSON.stringify(settingToCompare.field_value);
    const fieldValueHasChanged = startingValue !== currentValue;

    return fieldValueHasChanged;
  });
};

function ExpandedIntegrationForm({
  integrationName,
  integration,
  saving,
  hasChanges,
  isRemovingIntegration,
  onSubmit = () => {},
  setHasChanges = () => {},
  handleAuthenticate = () => {},
}) {
  const integrationSettings = integration.settings;
  const [clonedSettings, setClonedSettings] = useState([[], []]);

  const methods = useForm({
    reValidateMode: "all",
  });

  useEffect(() => {
    if (integrationSettings) {
      setClonedSettings(
        integrationSettings
          .reduce(
            (orderedColumns, setting) => {
              setting.includesInInitialAuthFlexRows = integration?.initialAuthFlexRows?.some(
                (initialAuthFlexRow) =>
                  initialAuthFlexRow.settingName === setting.implementation_method_name
              );
              const column = setting?.metadata?.column ? setting.metadata.column - 1 : 0;
              if (
                !Number.isNaN(Number(setting?.metadata?.order)) &&
                !orderedColumns[column][setting?.metadata?.order]
              ) {
                orderedColumns[column][setting?.metadata?.order] = setting;
              } else {
                orderedColumns[column].push(setting);
              }
              return orderedColumns;
            },
            [[], []]
          )
          .map((orderedColumn) =>
            orderedColumn
              .filter((item) => item)
              .sort((a, b) => {
                const orderA = a?.metadata?.order;
                const orderB = b?.metadata?.order;

                const isValidOrderA = !Number.isNaN(orderA);
                const isValidOrderB = !Number.isNaN(orderB);

                if (isValidOrderA && isValidOrderB) {
                  if (orderA < orderB) return -1;
                  if (orderA > orderB) return 1;
                  return 0;
                }
                if (isValidOrderA) return -1;
                if (isValidOrderB) return 1;
                return 0;
              })
          )
      );
    }
  }, [integrationSettings]);

  const {
    estimatedApiUsage: estimatedDailyApiUsage,
    actualApiUsage,
    externalApiData,
    oAuthStartUrl,
    showAuthenticateButton,
    showClientCredentialsButton,
    showAuthenticatedIcon,
  } = integration;

  const showApiUsage = !!(estimatedDailyApiUsage || actualApiUsage || externalApiData);

  const checkKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  function filterDuplicatesInData(data) {
    const result = {};

    // Helper function to find duplicates in an array
    const getDuplicateValues = (arr) => {
      const valueCount = {};
      const duplicates = new Set();

      arr.forEach((value) => {
        valueCount[value] = (valueCount[value] || 0) + 1;
        if (valueCount[value] > 1) {
          duplicates.add(value);
        }
      });

      return Array.from(duplicates);
    };

    // Process each top-level entry in the data
    Object.entries(data).forEach(([key, section]) => {
      // Process each sub-section
      Object.entries(section).forEach(([subKey, array]) => {
        const duplicates = getDuplicateValues(array);

        // Only add to result if duplicates are found
        if (duplicates.length > 0) {
          if (!result[key]) {
            result[key] = {};
          }
          result[key][subKey] = duplicates;
        }
      });
    });

    return result;
  }

  const handleOnSubmit = (passedClonedSettings) => {
    const data = reconcileDiff(
      integrationSettings,
      passedClonedSettings && Array.isArray(passedClonedSettings)
        ? passedClonedSettings.flat()
        : clonedSettings.flat()
    );

    // Validate Duplicate entries in table
    if (
      data.length &&
      data[0].settings_data_type === "table" &&
      !!data[0].metadata.preventDuplicateEntries
    ) {
      const duplicateKeyLabels = {};

      const processedDuplicateGroupedValueDict = {};

      const tableRows = data[0].field_value.rows;

      tableRows.forEach((tableRow) => {
        tableRow.forEach((row) => {
          if (row.duplication) {
            if (
              data[0].metadata.preventDuplicateEntries?.[row.duplication.duplicateKey]?.[
                row.columnId
              ]
            ) {
              if (!processedDuplicateGroupedValueDict[row.duplication.duplicateKey]) {
                processedDuplicateGroupedValueDict[row.duplication.duplicateKey] = {};

                if (!duplicateKeyLabels[row.duplication.duplicateKey])
                  duplicateKeyLabels[row.duplication.duplicateKey] = tableRow[0].field_label;
              }

              if (!processedDuplicateGroupedValueDict[row.duplication.duplicateKey][row.columnId])
                processedDuplicateGroupedValueDict[row.duplication.duplicateKey][row.columnId] = [];

              processedDuplicateGroupedValueDict[row.duplication.duplicateKey][row.columnId].push(
                row?.field_value?.value
              );
            }
          }
        });
      });

      const duplicateData = filterDuplicatesInData(processedDuplicateGroupedValueDict);

      if (duplicateData && Object.keys(duplicateData).length)
        return enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: `Duplicate entries found in ${Object.keys(duplicateData).map(
              (key) => duplicateKeyLabels[key] || key
            )}`,
          }),
          {
            variant: "custom",
          }
        );
    }

    return onSubmit(data);
  };

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <FormProvider {...methods}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        onSubmit={methods.handleSubmit(handleOnSubmit)}
        onKeyDown={(e) => checkKeyDown(e)}
        autoComplete="off"
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          {integration?.initialAuthFlexRows?.length ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-evenly",
                gap: "20px",
              }}
            >
              {integration.initialAuthFlexRows.map(({ settingName, column }) => {
                if (settingName === "showAuthenticateButton" && !!showAuthenticateButton) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignSelf: "flex-start",
                      }}
                    >
                      <MuiLink href={oAuthStartUrl}>
                        <MDButton color="newColor">
                          Connect to {integration.integrationName}
                        </MDButton>
                      </MuiLink>
                    </Box>
                  );
                }

                const columnIndex = column ? column - 1 : 0;
                const clonedSettingIndex = clonedSettings[columnIndex]?.findIndex(
                  (setting) => setting.implementation_method_name === settingName
                );

                if (clonedSettingIndex < 0) return null;

                const setting = clonedSettings[columnIndex][clonedSettingIndex];
                if (!setting) return null;

                const IntegrationSetting =
                  settingsDataTypeToComponent[setting.settings_data_type].Component;
                const extraParams = {};

                // eslint-disable-next-line no-prototype-builtins
                if (setting?.metadata?.hasOwnProperty("allowDuplicateMappings")) {
                  if (!extraParams.options) extraParams.options = {};
                  extraParams.options.allowDuplicateMappings =
                    setting.metadata.allowDuplicateMappings;
                }

                return (
                  <IntegrationSetting
                    key={setting.id}
                    setting={setting}
                    integrationName={integrationName}
                    setParentState={(newValue, shouldSubmit = false) => {
                      setClonedSettings((prev) => {
                        const clone = cloneDeep(prev);
                        clone[columnIndex][clonedSettingIndex].field_value = newValue;

                        if (shouldSubmit) {
                          handleOnSubmit(clone);
                        }

                        return clone;
                      });

                      if (!shouldSubmit) {
                        setHasChanges(true);
                      }
                    }}
                    {...extraParams}
                    styles={{
                      width: "fit-content",
                      flexGrow: setting.settings_data_type === "boolean" ? 0 : 1,
                    }}
                  />
                );
              })}
            </Box>
          ) : null}
          <Box
            sx={{
              width: "100%",
            }}
          >
            {clonedSettings[0].map((setting, clonedSettingIndex) => {
              if (setting?.includesInInitialAuthFlexRows) return null;
              const IntegrationSetting =
                settingsDataTypeToComponent[setting.settings_data_type].Component;
              const extraParams = {};

              // eslint-disable-next-line no-prototype-builtins
              if (setting?.metadata?.hasOwnProperty("allowDuplicateMappings")) {
                if (!extraParams.options) extraParams.options = {};
                extraParams.options.allowDuplicateMappings =
                  setting.metadata.allowDuplicateMappings;
              }

              return (
                <IntegrationSetting
                  key={setting.id}
                  setting={setting}
                  integrationName={integrationName}
                  setParentState={(newValue, shouldSubmit = false) => {
                    setClonedSettings((prev) => {
                      const clone = cloneDeep(prev);
                      clone[0][clonedSettingIndex].field_value = newValue;

                      if (shouldSubmit) {
                        handleOnSubmit(clone);
                      }

                      return clone;
                    });

                    if (!shouldSubmit) {
                      setHasChanges(true);
                    }
                  }}
                  {...extraParams}
                />
              );
            })}
          </Box>
          <Box>
            {clonedSettings[1].map((setting, clonedSettingIndex) => {
              if (setting?.includesInInitialAuthFlexRows) return null;
              const IntegrationSetting =
                settingsDataTypeToComponent[setting.settings_data_type].Component;
              const extraParams = {};

              // eslint-disable-next-line no-prototype-builtins
              if (setting?.metadata?.hasOwnProperty("allowDuplicateMappings")) {
                if (!extraParams.options) extraParams.options = {};
                extraParams.options.allowDuplicateMappings =
                  setting.metadata.allowDuplicateMappings;
              }

              return (
                <IntegrationSetting
                  key={setting.id}
                  setting={setting}
                  integrationName={integrationName}
                  setParentState={(newValue, shouldSubmit = false) => {
                    setClonedSettings((prev) => {
                      const clone = cloneDeep(prev);
                      clone[1][clonedSettingIndex].field_value = newValue;

                      if (shouldSubmit) {
                        handleOnSubmit(clone);
                      }
                      return clone;
                    });

                    if (!shouldSubmit) {
                      setHasChanges(true);
                    }
                  }}
                  {...extraParams}
                />
              );
            })}
          </Box>
        </Box>
        {!!showAuthenticateButton &&
          !integration?.initialAuthFlexRows?.some(
            (initialAuthFlexRow) => initialAuthFlexRow.settingName === "showAuthenticateButton"
          ) && (
            <Box
              sx={{
                marginTop: "2rem",
                marginBottom: "2rem",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <MuiLink href={oAuthStartUrl}>
                <MDButton color="newColor">Connect to {integration.integrationName}</MDButton>
              </MuiLink>
            </Box>
          )}
        {!!showClientCredentialsButton && (
          <Box
            sx={{
              marginTop: "2rem",
              marginBottom: "2rem",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <MDButton color="newColor" onClick={handleAuthenticate}>
              Authentice with {integration.integrationName}
            </MDButton>
          </Box>
        )}
        {!!showAuthenticatedIcon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: "16px",
            }}
          >
            <Icon fontSize="large" sx={{ fontWeight: "bold", color: "green", marginRight: "8px" }}>
              check_circle
            </Icon>
            <Typography sx={{ color: "green", paddingTop: "2px" }}>
              Connected to {integration.integrationName}
            </Typography>
          </Box>
        )}
        {showApiUsage && (
          <Box
            sx={{
              height: "100px",
            }}
          >
            <Bar
              height="100%"
              width="100%"
              options={{
                indexAxis: "y",
                plugins: {
                  title: {
                    display: true,
                    text: `${integration.integrationName} Api Usage`,
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                  },
                },
                maintainAspectRatio: false,
              }}
              data={{
                labels: [""],
                datasets: [
                  {
                    label: "Estimated Daily Calls",
                    data: [estimatedDailyApiUsage],
                    backgroundColor: "green",
                    stack: "Stack 0",
                  },
                  {
                    label: "Calls Since Midnight",
                    data: [actualApiUsage],
                    backgroundColor: "red",
                    stack: "Stack 0",
                  },
                  {
                    label: "24 hour api limit",
                    data: [externalApiData?.dailyRequestLimit],
                    backgroundColor: "#cdcdcd",
                    stack: "Stack 0",
                  },
                ],
              }}
            />
          </Box>
        )}
        {hasChanges && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "3rem",
            }}
          >
            <MDButton
              onClick={() => {
                setClonedSettings(
                  integrationSettings
                    .reduce(
                      (orderedColumns, setting) => {
                        const column = setting?.metadata?.column ? setting.metadata.column - 1 : 0;
                        if (!Number.isNaN(Number(setting?.metadata?.order))) {
                          orderedColumns[column][setting?.metadata?.order] = setting;
                        } else {
                          orderedColumns[column].push(setting);
                        }
                        return orderedColumns;
                      },
                      [[], []]
                    )
                    .map((orderedColumn) => orderedColumn.filter((item) => item))
                );
                setHasChanges(false);
                methods.clearErrors();
              }}
              disabled={saving}
              variant="gradient"
              color="secondary"
              sx={{
                marginRight: "1rem",
              }}
            >
              Cancel
            </MDButton>
            <MDButton
              type="submit"
              disabled={saving || isRemovingIntegration}
              variant="gradient"
              color="newColor"
            >
              Save
            </MDButton>
          </Box>
        )}
      </form>
    </FormProvider>
  );
}

export default ExpandedIntegrationForm;
